import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Theme from '../../../../theme';
import { BaseModal } from 'components/UI';
import { CSVReader } from 'react-papaparse';
import { BsPaperclip } from 'react-icons/bs';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import useStyles from './VehicleBulkUploadModal.styled';
import { vehicleActions } from 'state/modules/vehicles';
import { Box, Button, Typography } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { integrations } from 'config';

const VehicleBulkUploadModal = ({ open, handleClick }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvReaderRef = useRef(null);
  const [file, setFile] = useState();
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState(0);
  const [columns, setColumns] = useState({});
  const [errors, setErrors] = useState(null);
  const { fetchingBulkUpload, fetchedBulkUpload } = useSelector((state) => state.vehicles);
  const { currentOrganization } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.config);

  const csvData = () => {
    let _data = [];
    columns.forEach((col) => {
      _data.push({ label: col });
    });
    return _data;
  };

  const handleNext = () => {
    let formData = new FormData();
    formData.append('vehicles_file', file);
    dispatch(vehicleActions.fetchBulkUpload(formData));
  };

  const onReplaceFile = () => {
    setFile(null);
    setErrors(null);
    if (csvReaderRef?.current) {
      csvReaderRef.current.removeFile();
    }
  };

  const handleOnDrop = (data, file) => {
    setFile(file);
    const columns = data?.[0]?.data;

    if (
      !columns.includes('vehicle_type_id') ||
      !(columns.includes('vehicle_group_id') || columns.includes('fleet_id'))
    ) {
      setErrors(i18n.t('vehicle.import_csv.csv_fields_required'));
    }

    if (currentOrganization.organization_type === 'OWNER' && columns.includes('fleet_id')) {
      columns[columns.indexOf('fleet_id')].replace('fleet_id', 'vehicle_group_id');
    }

    setColumns(columns);
    setRows(data.length - 1);
    if (
      !columns.includes('vehicle_type_id') ||
      !(columns.includes('vehicle_group_id') || columns.includes('fleet_id'))
    ) {
      setErrors(i18n.t('vehicle.import_csv.csv_fields_required'));
    }
  };

  const handleCloseModal = () => {
    handleClick();
    setStep(1);
    dispatch(vehicleActions.resetBulkUpload());
  };

  useEffect(() => {
    if (fetchedBulkUpload) {
      setStep(2);
    }
  }, [fetchedBulkUpload]);

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton={false}
      hasCancelButton={false}
      enableCloseButton={false}
      handleClick={handleCloseModal}
      title={
        step === 1
          ? i18n.t('vehicle.import_csv.title')
          : i18n.t('vehicle.import_csv.import_external_data')
      }
    >
      {step === 1 ? (
        <>
          <span className={classes.csvDescription}>{i18n.t('vehicle.import_csv.description')}</span>
          <span className={classes.csvMaxRows}>{i18n.t('vehicle.import_csv.max_rows')}</span>
          <div className={file ? classes.readerHide : classes.container}>
            <CSVReader
              ref={csvReaderRef}
              onDrop={handleOnDrop}
              style={{
                dropArea: {
                  backgroundColor: '#F2F2F2',
                  border: '1px dashed #A8A8A8',
                  borderColor: '#F2F2F2',
                  borderRadius: 0,
                  height: 132,
                },
                dropAreaActive: {
                  border: `2px dashed ${Theme.palette.primary.main}`,
                },
                dropFile: {
                  display: 'none',
                },
              }}
              noProgressBar
              className={classes.draggableContainer}
            >
              <Box className={classes.uploadTextContainer}>
                <CloudUploadIcon className={classes.uploadIcon} />
                <span className={classes.uploadText}>
                  {i18n.t('vehicle.import_csv.upload_text')}
                  <span className={classes.browse}>{i18n.t('vehicle.import_csv.browse')}</span>
                </span>
              </Box>
            </CSVReader>
          </div>
          {file && (
            <Box>
              <Box display="flex" alignItems="center">
                <BsPaperclip size={20} color="#4D4F5C" />
                <Typography className={classes.fileName} variant="body2" noWrap>
                  {file?.name}
                </Typography>
              </Box>
              <Button
                color="primary"
                onClick={onReplaceFile}
                style={{ fontWeight: 500 }}
                className={classes.inlineAction}
              >
                {i18n.t('documents.replace_file')}
              </Button>
              {errors && (
                <Box display="flex" mb={1} alignItems="center">
                  <CloseIcon className={classes.errorIcon} />
                  <Typography className={classes.fieldText}>{errors}</Typography>
                </Box>
              )}
            </Box>
          )}
          <Box className={classes.downloadContainer}>
            <GetAppIcon className={classes.downloadIcon} />
            <div>
              <span>{i18n.t('vehicle.import_csv.download_text')}</span>
              <a
                href={`/files/${
                  settings?.core?.subscription?.enabled
                    ? 'vehicle_bulk_upload_flat'
                    : 'vehicle_bulk_upload'
                }.csv`}
                download={`vehicle_bulk_upload.csv`}
                className={classes.downloadLink}
              >
                {i18n.t('vehicle.import_csv.download_csv')}
              </a>
            </div>
          </Box>
          <Box className={classes.buttonContainers}>
            <Button
              size="large"
              variant="text"
              color="primary"
              onClick={handleCloseModal}
              style={{ fontWeight: 500 }}
            >
              {i18n.t('common.cancel')}
            </Button>
            <Button
              size="large"
              variant="text"
              color="primary"
              onClick={handleNext}
              style={{ fontWeight: 500 }}
              disabled={!file || fetchingBulkUpload || errors ? true : false}
            >
              {i18n.t('common.next')}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box>
            <Box display="flex" flexWrap="wrap" mt={2}>
              <Box display="flex" alignItems="center" mb={3}>
                <CheckCircleIcon className={classes.successIcon} />
                <Typography className={classes.filename}>
                  {file.name} {i18n.t('vehicle.import_csv.upload_success')}
                </Typography>
              </Box>
              {csvData().map((field, i) => (
                <Box display="flex" width="50%" mb={1} key={i}>
                  <CheckIcon className={classes.successIcon} />
                  <Typography className={classes.fieldText}>
                    {i18n.t('vehicle.import_csv.matched_text')}{' '}
                    <span className={classes.field}>
                      {i18n.t(`vehicle.import_csv.${field.label}`)}
                    </span>{' '}
                    {i18n.t('vehicle.import_csv.field')}
                  </Typography>
                </Box>
              ))}
              <Box display="flex" width="50%" mb={1}>
                <CheckIcon className={classes.successIcon} />
                <Typography className={classes.fieldText}>
                  {rows} {i18n.t('vehicle.import_csv.row_detected')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box className={classes.buttonContainers}>
            <Button
              size="large"
              variant="text"
              color="primary"
              onClick={handleCloseModal}
              style={{ fontWeight: 500 }}
            >
              {i18n.t('common.cancel')}
            </Button>
            <Button
              size="large"
              variant="text"
              color="primary"
              onClick={handleCloseModal}
              style={{ fontWeight: 500 }}
            >
              {i18n.t('common.continue')}
            </Button>
          </Box>
        </>
      )}
    </BaseModal>
  );
};

VehicleBulkUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

VehicleBulkUploadModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default VehicleBulkUploadModal;
